import React from "react";

import WorkspaceToolRail from "./WorkspaceToolRail";
import resolveToolPanel, { PRODUCTION_SELECTABLE_TOOLS } from "./WorkspaceToolPanels";
import { CONTEXT_TOOLS_DEFERRED } from "../workspaceFrameCopy";

/**
 * The persistent right context/tools region
 * (WORKSPACE-FEATURE-001 S5.1 layer 3, S14; authoritative original
 * `WorkspaceAgentToolsCard` at commit 23d500e27; reference
 * `docs/design/prototypes/workspace-v1-reference.png`).
 *
 * AW-VIS-3 GAVE THE RESTORED RAIL ITS BEHAVIOUR BACK
 * --------------------------------------------------
 * AW-VIS-2 restored this region's geometry and then withheld every prop
 * the rail needs to operate -- no `selectable`, no `onSelectTool`, no
 * `onToggleExpanded`, no `panelContent` -- so all eight tools and the
 * collapse control rendered inert. That was the right call while the
 * only available panel content was fixture data. It stopped being the
 * right call once it was clear that selecting a tool, opening its panel
 * and collapsing the rail are presentation acts with no data dependency
 * at all.
 *
 * So this component now forwards what the shell owns, and the rail does
 * what it was always written to do. It is still presentation: no state
 * of its own, no fetch, no effect. The shell holds `activeTool` and
 * `isExpanded`; `WorkspaceToolPanels` decides what a selected tool
 * renders; this file wires the two together and nothing else.
 *
 * WHAT IS STILL WITHHELD, AND WHY
 * -------------------------------
 * Seven of the eight panels are honest deferred copy. Six are blocked
 * on the same thing -- the workspace holds no lead identity, by design
 * (`getCallState` returns `{phase, refusal, lead_retained}` and nothing
 * more), and AW-2C owns that. Actions and History were never
 * implemented in the original shell either.
 *
 * No fixture panel is reachable from here: this file imports no
 * `workspaceFixtures`, no `projectionContract`, and no
 * `components/panels/*`. `WorkspaceAgentToolsCard` and its eight
 * fixture-bound panels remain development-only and are named in the
 * Production fixture-firewall guard.
 *
 * AI INSIGHTS IS NOT HERE, AND MUST NOT BE
 * ----------------------------------------
 * The committed reference mockup shows an "AI Insights" tool with a
 * "New" badge. S14.2 and S12.12 prohibit an AI tool, icon, placeholder
 * or disabled panel outright. That is a prohibition on the capability,
 * not a deferral pending a contract, so neither the scaffolding policy
 * nor selectability reaches it. The specification governs the mockup.
 */
export default function WorkspaceContextDock({
  activeTool = null,
  onSelectTool = null,
  isExpanded = true,
  onToggleExpanded = null,
  toolData = null,
  customerData = null,
}) {
  return (
    <WorkspaceToolRail
      selectable={PRODUCTION_SELECTABLE_TOOLS}
      activeTool={activeTool}
      onSelectTool={onSelectTool}
      isExpanded={isExpanded}
      onToggleExpanded={onToggleExpanded}
      panelContent={resolveToolPanel(activeTool, toolData, customerData)}
      unavailableReason={CONTEXT_TOOLS_DEFERRED}
    />
  );
}
